import { format, isSameDay, Locale, subDays } from 'date-fns'
import { useMemo } from 'react'
import { getFnsLocale } from '../intl'
import { isTwelveHoursFormat } from '../local-time'

export const formatRelative = (date: Date): string => {
	const now = new Date()

	if (isSameDay(now, date)) {
		return 'date_format.today'
	} else if (isSameDay(subDays(now, 1), date)) {
		return 'date_format.yesterday'
	} else {
		return format(date, 'yyyy-MM-dd')
	}
}

/**
 * A clock time, 12- or 24-hour the way the user's region writes it - the rule
 * `localizedTimeFormat` follows, so a chat message and the agenda beside it
 * agree. date-fns' `'p'` would follow the UI language instead, and to
 * date-fns every English UI is en-US.
 */
export const formatClockTime = (date: Date, locale?: Locale): string =>
	format(date, isTwelveHoursFormat() ? 'h:mm a' : 'HH:mm', { locale })

type DateFormatters = {
	formatTime: (date?: Date | null) => string
	formatDate: (
		date?: Date | null,
		format?: 'LONG' | 'SHORT' | 'RELATIVE'
	) => string
	format: (date: Date | null | undefined, format: string) => string
}
export const useDateFormat = (): DateFormatters => {
	const locale = getFnsLocale()

	return useMemo<DateFormatters>(
		() => ({
			formatTime: date => (date ? formatClockTime(date, locale) : ''),
			formatDate: (date, formatType = 'SHORT') => {
				if (!date) return ''
				switch (formatType) {
					case 'LONG':
						// MMMM, not LLLL: after a day number Polish takes the
						// genitive ("2 października"), not the standalone form.
						return format(date, 'eeee - dd MMMM', { locale })
					case 'SHORT':
						return format(date, 'do MMM', { locale })
					case 'RELATIVE':
						return formatRelative(date)
				}
			},
			format: (date, fmt) => (date ? format(date, fmt, { locale }) : ''),
		}),
		[locale]
	)
}

export default formatRelative
